import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EyeOff, MessageSquare } from "lucide-react";
import "./past.css";
import { usePast } from "./past.functions";
import { formatConversationTimestamp } from "../../components/conversation-list/conversation-list.functions";
import { ConfirmActionDialog } from "../../components/dialogs";
import { ProfileAvatar } from "../../components/profile-display";
import { buildPastChatNavigation } from "./past.functions";

function PastInner() {
  const navigate = useNavigate();
  const { chats, chatListLoading, hideConversation } = usePast();
  const hasHydratedListRef = useRef(false);
  const [hideTargetChatId, setHideTargetChatId] = useState("");
  const [hideConfirmSubmitting, setHideConfirmSubmitting] = useState(false);

  useEffect(() => {
    if (!chatListLoading) {
      hasHydratedListRef.current = true;
    }
  }, [chatListLoading]);

  const showInitialBlockingLoad =
    chatListLoading &&
    !hasHydratedListRef.current &&
    chats.length === 0;

  const goToChat = (chat) => {
    const target = buildPastChatNavigation(chat);
    navigate(target.pathname, { state: target.state });
  };

  const openHideConversationConfirm = (chatPk) => {
    const normalizedChatId = String(chatPk || "").trim();
    if (!normalizedChatId) return;
    setHideTargetChatId(normalizedChatId);
  };

  const closeHideConversationConfirm = () => {
    if (hideConfirmSubmitting) return;
    setHideTargetChatId("");
  };

  const confirmHideConversation = async () => {
    const chatId = String(hideTargetChatId || "").trim();
    if (!chatId || hideConfirmSubmitting) return;
    setHideConfirmSubmitting(true);
    try {
      await hideConversation(chatId);
      setHideTargetChatId("");
    } finally {
      setHideConfirmSubmitting(false);
    }
  };

  return (
    <div className="pastComms">
      <div className="pastComms__container">
        {showInitialBlockingLoad ? (
          <div className="pastComms__muted">
            <MessageSquare size={32} className="pastComms__mutedIcon" />
            <p className="pastComms__mutedTitle">Loading past conversations…</p>
          </div>
        ) : chats.length === 0 ? (
          <div className="pastComms__muted">
            <MessageSquare size={32} className="pastComms__mutedIcon" />
            <p className="pastComms__mutedTitle">No past conversations yet.</p>
            <p className="pastComms__mutedText">
              Conversations you end will show up here so you can revisit them later.
            </p>
          </div>
        ) : (
          <section className="pastComms__section">
            <div className="pastComms__list">
              {chats.map((chat) => {
                const isOnline = chat?.presenceStatus === "online";
                const hasUnread = Number(chat?.unreadCount || 0) > 0;
                const chatName = String(chat?.title || chat?.name || "Conversation").trim();
                const chatPreview = String(
                  chat?.previewText || chat?.lastMessage || "Open conversation history…"
                ).trim();
                const timestampLabel = formatConversationTimestamp(
                  chat?.lastUpdatedAt || chat?.timestamp,
                  chat?.timestamp
                );
                const onOpenChat = () => goToChat(chat);
                const onCardKeyDown = (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpenChat();
                  }
                };

                return (
                  <article
                    key={chat.id || chat.chatPk || chatName}
                    className={`pastComms__card ${
                      hasUnread ? "pastComms__card--unread" : "pastComms__card--read"
                    }`}
                    onClick={onOpenChat}
                    onKeyDown={onCardKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open past chat with ${chatName}`}
                  >
                    <div className="pastComms__cardRow">
                      <div className="pastComms__avatarWrap">
                        <ProfileAvatar user={chat} name={chatName} className="pastComms__avatarImg" />
                        {!chat?.isGroup ? (
                          <span
                            className={`pastComms__presenceDot ${
                              isOnline
                                ? "pastComms__presenceDot--online"
                                : "pastComms__presenceDot--offline"
                            }`}
                            aria-hidden="true"
                          />
                        ) : null}
                      </div>

                      <div className="pastComms__body">
                        <div className="pastComms__topRow">
                          <h2 className={`pastComms__name ${
                            hasUnread ? "pastComms__name--unread" : "pastComms__name--read"
                          }`}>
                            {chatName}
                          </h2>
                          <div className="pastComms__meta">
                            <div className="pastComms__metaStatic">
                              {timestampLabel ? (
                                <span className="pastComms__timestamp">{timestampLabel}</span>
                              ) : null}
                              {hasUnread ? (
                                <span className="pastComms__unreadDot" aria-label="Unread messages" />
                              ) : null}
                            </div>
                            <div className="pastComms__metaActions">
                              <button
                                type="button"
                                className="pastComms__toolBtn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openHideConversationConfirm(chat.chatPk);
                                }}
                                aria-label={`Hide chat with ${chatName}`}
                              >
                                <EyeOff size={16} />
                              </button>
                            </div>
                          </div>
                        </div>

                        <p className={`pastComms__message ${
                          hasUnread ? "pastComms__message--unread" : "pastComms__message--read"
                        }`}>{chatPreview}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Hide conversation confirmation */}
      <ConfirmActionDialog
        isOpen={Boolean(hideTargetChatId)}
        title="Hide Chat?"
        message="You are about to hide this chat. You can restore it anytime in Settings."
        confirmLabel="Hide Chat"
        cancelLabel="Cancel"
        onClose={closeHideConversationConfirm}
        onConfirm={confirmHideConversation}
        submitting={hideConfirmSubmitting}
      />
    </div>
  );
}

export default function Past() {
  return <PastInner />;
}
